import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  const LINKS = [
    { key: "buchung",   label: t("nav.buchung"),    to: "/buchung" },
    { key: "schulungen",label: t("nav.schulungen"), to: "/schulungen" },
    { key: "academy",   label: t("nav.academy"),    to: "/academy" },
    { key: "kontakt",   label: t("nav.kontakt"),    to: "/kontakt" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      {/* Utility bar (language switcher, minimalist) */}
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-7 flex items-center justify-end border-b border-border/40">
        <LanguageSwitcher />
      </div>

      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src="/logo-dark.png" alt="Maschas Atelier" className="h-7 md:h-8 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-primary ${
                pathname === l.to ? "text-primary" : "text-foreground/70"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="header-book-btn"
            onClick={() => navigate("/buchung")}
            className="group relative overflow-hidden border border-foreground px-6 py-2.5 text-sm tracking-wide"
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-primary-foreground">
              {t("nav.buchen_cta")}
            </span>
            <span className="absolute inset-0 bg-primary translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0" />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
        >
          {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-background border-b border-border px-5 pb-8 pt-2">
          <nav className="flex flex-col">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.key}`}
                className="font-serif text-3xl py-3 border-b border-border/60"
              >
                {l.label}
              </Link>
            ))}
            <button
              data-testid="mobile-book-btn"
              onClick={() => navigate("/buchung")}
              className="mt-6 bg-primary text-primary-foreground py-4 text-sm tracking-wide"
            >
              {t("nav.buchen_cta")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
